import * as React from "react";
import { useTranslation } from "react-i18next";

import { Box, Button, Dialog, Typography } from "@mui/material";
import axios from "axios";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { COLORS } from "./constants";
import ScanHelpTooltip from "./ScanHelpTooltip";
import { resolveSlot } from "../../../utils/slotMapping";

interface ConfirmActivateModalProps {
  open: boolean;
  onClose: () => void;
  socket: any;
  apiBaseURL: string;
  shopId: any;
  onConfirmedActivated: () => void;
}

const SCREENS = [1, 2]; // the product supports two screens per shop

// Confirm & Activate popup (Figma node 2201:1652). Reached from the Sale-date
// detail screen. A brand new ticket is passed straight through CONFIRM -> ACTIVE
// and placed on the given slot, so it can be sold the same day. Stays open after
// each success so several tickets can be entered back to back.
const ConfirmActivateModal: React.FC<ConfirmActivateModalProps> = ({
  open,
  onClose,
  socket,
  apiBaseURL,
  shopId,
  onConfirmedActivated,
}) => {
  //@ts-ignore
  const config = window.config;
  const defaultPerScreen = Number(config?.ticketsPerScreen ?? 0);
  const { t } = useTranslation("translation");
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();

  const [scanCode, setScanCode] = React.useState<string>("");
  const [gameNumber, setGameNumber] = React.useState<string>("");
  const [packNumber, setPackNumber] = React.useState<string>("");
  const [slotNumber, setSlotNumber] = React.useState<string>("");
  const [perScreenCounts, setPerScreenCounts] = React.useState<number[]>([]);
  const [submitting, setSubmitting] = React.useState<boolean>(false);

  const clearFields = () => {
    setScanCode("");
    setGameNumber("");
    setPackNumber("");
    setSlotNumber("");
  };

  React.useEffect(() => {
    if (!open) return;
    Promise.all(
      SCREENS.map((screenNumber) =>
        axios
          .get(`${apiBaseURL}/api/settings/fetchSettings?id=${shopId}&screenNumber=${screenNumber}`)
          .then((res: any) => Number(res?.data?.settings?.ticketsCount ?? defaultPerScreen) || 0)
          .catch(() => 0),
      ),
    ).then(setPerScreenCounts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiBaseURL, shopId]);

  React.useEffect(() => {
    if (!socket) return;

    const onSuccess = () => {
      setSubmitting(false);
      showSuccessMessage(t("confirmActivateSuccessMsg", "Ticket confirmed and activated successfully"));
      clearFields();
      onConfirmedActivated();
    };
    const onError = (response: any) => {
      setSubmitting(false);
      showErrorMessage(response?.message ?? t("somethingWentWrong", "Something went wrong"));
    };

    socket.on("confirmAndActivateInventoryV2Response", onSuccess);
    socket.on("confirmAndActivateInventoryV2Error", onError);
    return () => {
      socket.off("confirmAndActivateInventoryV2Response", onSuccess);
      socket.off("confirmAndActivateInventoryV2Error", onError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  // Reuse the documented barcode split (game = first 4, pack = next 7)
  const onScanChange = (value: string) => {
    setScanCode(value);
    if (value.length >= 11) {
      setGameNumber(value.substring(0, 4));
      setPackNumber(value.substring(4, 11));
    }
  };

  const handleSubmit = () => {
    if (!socket) {
      return showErrorMessage(t("socketNotReady", "Connection not ready, please retry"));
    }
    if (!/^\d{4}$/.test(gameNumber)) {
      return showErrorMessage(t("gameNumberFourDigitsError", "The game number must be 4 digits."));
    }
    if (!/^\d{7}$/.test(packNumber)) {
      return showErrorMessage(t("packNumberSevenDigitsError", "The pack number should be 7 digits."));
    }
    const slot = Number(slotNumber);
    if (!Number.isInteger(slot) || slot <= 0) {
      return showErrorMessage(t("slotNumberError", "Please enter a valid slot number"));
    }
    const resolved = resolveSlot(slot, perScreenCounts);
    if (!resolved) {
      return showErrorMessage(t("slotOutOfRange", "Slot number is out of the configured screen range"));
    }

    setSubmitting(true);
    socket.emit("confirmAndActivateInventoryV2", {
      shopId,
      gameNumber,
      packNumber,
      screenNumber: resolved.screenNumber,
      ticketPosition: resolved.ticketPosition,
    });
  };

  const onEntryKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (!submitting) handleSubmit();
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const labelSx = { fontSize: "18px", color: "#434343", mb: "6px" } as const;
  const inputSx = {
    width: "100%",
    height: "40px",
    borderRadius: "10px",
    border: `1px solid ${COLORS.border}`,
    backgroundColor: "#fff",
    px: "12px",
    outline: "none",
    fontSize: "17px",
  } as const;

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        handleClose();
      }}
      PaperProps={{
        sx: {
          width: "505px",
          maxWidth: "95vw",
          borderRadius: "10px",
          border: `2px solid ${COLORS.orange}`,
          backgroundColor: COLORS.panelBg,
          overflow: "hidden",
        },
      }}
    >
      {/* Orange header */}
      <Box sx={{ backgroundColor: COLORS.orange, height: "43px", display: "flex", alignItems: "center", justifyContent: "space-between", px: 2 }}>
        <Typography sx={{ color: "#fff", fontSize: "22px", fontWeight: 600, letterSpacing: "1px" }}>
          {t("confirmActivateGameTitle", "Confirm & Activate Game")}
        </Typography>
        <Box
          component='span'
          onClick={handleClose}
          sx={{ color: "#fff", fontSize: "26px", cursor: "pointer", lineHeight: 1 }}
          aria-label={t("close", "Close")}
        >
          ×
        </Box>
      </Box>

      <Box sx={{ p: 2.5 }}>
        <Box mb={1.5}>
          <Typography sx={labelSx}>{t("scanCodeLabel", "Scan Code")}</Typography>
          <Box display='flex' alignItems='center' gap='8px'>
            <Box component='input' value={scanCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => onScanChange(e.target.value)} sx={inputSx} />
            <ScanHelpTooltip />
          </Box>
        </Box>
        {/* Orange divider separating scan entry from manual entry */}
        <Box sx={{ height: "2px", backgroundColor: COLORS.orange, mx: -2.5, mb: 2.5 }} />
        <Box mb={2}>
          <Typography sx={labelSx}>{t("gameNumberLabel", "Game Number")}</Typography>
          <Box component='input' value={gameNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setGameNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
        <Box mb={2}>
          <Typography sx={labelSx}>{t("packNumberLabel", "Pack Number")}</Typography>
          <Box component='input' value={packNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPackNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
        <Box mb={1}>
          <Typography sx={labelSx}>{t("slotNumberLabel", "Slot Number")}</Typography>
          <Box component='input' value={slotNumber} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSlotNumber(e.target.value)} onKeyDown={onEntryKeyDown} sx={inputSx} />
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ borderTop: `1px solid ${COLORS.orange}`, display: "flex", justifyContent: "flex-end", gap: "12px", p: 2 }}>
        <Button
          onClick={handleClose}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "2px", px: 3, fontSize: "16px", fontWeight: 600, letterSpacing: "1px", "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("close", "Close")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          disableElevation
          sx={{ backgroundColor: COLORS.navy, color: "#fff", textTransform: "none", borderRadius: "2px", px: 3, fontSize: "16px", fontWeight: 600, letterSpacing: "1px", "&:hover": { backgroundColor: COLORS.navy, opacity: 0.9 } }}
        >
          {t("confirmActivateButton", "Confirm & Activate")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default ConfirmActivateModal;
